"use client";

import { Suspense, useState } from "react";

import { AnimatePresence, motion } from "framer-motion";
import {
  BoxesIcon, ChevronDownIcon, ChevronUpIcon,
  PackageIcon, TruckIcon } from "lucide-react";
import { useTranslations } from "next-intl";

import { priceFormat } from "@/shared.utils/number-format";
import { IconButton } from "@/shared-web.ui/ui.icon-button";
import { Separator } from "@/shared-web.ui/ui.separator";
import { Skeleton } from "@/shared-web.ui/ui.skeleton";
import { Text } from "@/shared-web.ui/ui.text";
import { cn } from "@/shared-web.ui/util.cn";

import {
  type ServerDataOutput,
  humanWeightStr,
  useSubscriptionSummary,
} from "../data.subscription-data";

interface IconSummaryProps {
  data: ServerDataOutput;
}
const IconSummary = (props: IconSummaryProps) => {
  const { data } = props;

  const {
    subscriptionData: {
      subscriptionMeta: {
        packageMeta,
      },
    },
  } = data;

  const {
    selectedPackageKey,

    durationNum,
    frequencyNum,
    boxGrams,

    packageName,

    totalShipment,
    totalBeansGrams,
    totalShippingFee,
    totalProductStickerPrice,
    total,

    productStickerPrice,
    discountedPricePerBox,
  } = useSubscriptionSummary({ data });

  const t = useTranslations("stf_m_subscription.screen_subscription.summary_section");

  return (
    <div className={`
      grid grid-cols-2 gap-y-6
      xs:grid-cols-3
    `}
    >
      <div className="flex flex-col items-center">
        <div className="relative size-8">
          <PackageIcon className="text-gray-9 size-8" strokeWidth="1px" />
          <div className={`
            absolute right-0 top-0 -translate-y-1
            translate-x-1 text-2
          `}
          >
            {selectedPackageKey ? packageMeta[selectedPackageKey].emoji : ""}
          </div>
        </div>
        <div className={`
          flex h-12 flex-col items-center justify-center
        `}
        >
          <Text className="text-2/1" align="center">
            {packageName}
          </Text>
          <Text size="1" color="gray">
            {`≈ ${humanWeightStr({ grams: boxGrams })}`}
          </Text>
        </div>
        <Separator size="1" className="my-2" />
        <div className="flex flex-row items-baseline gap-1">
          <Text size="1" className="line-through" color="gray">
            {priceFormat(productStickerPrice)}
          </Text>
          <Text size="2" weight="medium" semanticColor="accent">
            {priceFormat(discountedPricePerBox)}
          </Text>
        </div>
      </div>
      <div className="flex flex-col items-center">
        <TruckIcon className="text-gray-9 size-8" strokeWidth="1px" />
        <div className={`
          flex h-12 flex-col items-center justify-center
        `}
        >
          <Text className="text-2/1">
            {t(
              "shipment_count",
              { count: totalShipment ?? 0 },
            )}
          </Text>
          <Text size="1" color="gray" align="center">
            {t("shipment_schedule",
              {
                frequency: frequencyNum ?? 0,
                duration: durationNum ?? 0,
              },
            )}
          </Text>
        </div>
        <Separator size="1" className="my-2" />
        <div className="flex flex-row items-baseline gap-1">
          <Text size="1" className="line-through" color="gray">
            {priceFormat(totalShippingFee)}
          </Text>
          <Text size="2" weight="medium" semanticColor="accent">
            {t("free_shipping")}
          </Text>
        </div>
      </div>
      <div className={`
        col-span-2 flex flex-col items-center
        xs:col-span-1
      `}
      >
        <BoxesIcon className="text-accent-11 size-8" strokeWidth="1px" />
        <div className="flex h-10 flex-col justify-center">
          <Text size="2" weight="medium" semanticColor="accent">
            {t("total_weight", { weight: humanWeightStr({ grams: totalBeansGrams }) })}
          </Text>
        </div>
        <Separator size="1" className="my-2" />
        <div className="flex flex-row items-baseline gap-1">
          <Text size="1" className="line-through" color="gray">
            {priceFormat(totalProductStickerPrice)}
          </Text>
          <Text size="2" weight="medium" semanticColor="accent">
            {priceFormat(total)}
          </Text>
        </div>
      </div>
    </div>
  );
};

interface TableSummaryProps {
  data: ServerDataOutput;
  tableType?: "full" | "head" | "tail";
}
const TableSummary = (props: TableSummaryProps) => {
  const { data, tableType = "full" } = props;

  const {
    durationNum,
    frequencyNum,
    boxGrams,

    totalShipment,
    totalShippingFee,
    totalProductStickerPrice,
    total,
    totalBeforeDiscount,

    discountedPricePerBox,

    productDiscount,
  } = useSubscriptionSummary({ data });

  const t = useTranslations("stf_m_subscription.screen_subscription.summary_section");

  return (
    <div className={`
      grid grid-cols-[minmax(100px,max-content)_1fr] gap-y-1
    `}
    >
      {(tableType === "full" || tableType === "head")
        ? (
            <>
              {/* Row */}
              <div className="flex flex-col">
                <Text size="3" color="gray">
                  {t("beans_subtotal")}
                </Text>
                <Text size="1" color="gray">
                  {t("beans_subtotal_description", {
                    grams: humanWeightStr({ grams: boxGrams }),
                    shipment: totalShipment || 0,
                  })}
                </Text>
              </div>
              <Text size="3" color="gray" align="right">
                {priceFormat(totalProductStickerPrice)}
              </Text>

              {/* Row */}
              <div className="flex flex-col">
                <Text size="3" color="gray">
                  {t("shipping_subtotal")}
                </Text>
                <Text size="1" color="gray">
                  {t("shipping_subtotal_description", {
                    totalShipment: totalShipment || 0,
                    frequencyNum: frequencyNum || 0,
                    durationNum: durationNum || 0,
                  })}
                </Text>
              </div>
              <Text size="3" color="gray" align="right">{priceFormat(totalShippingFee)}</Text>

              {/* Row */}
              <Text size="3" color="gray">
                {t("beans_discount")}
              </Text>
              <Text size="3" color="gray" align="right">
                -
                {priceFormat(productDiscount)}
              </Text>

              {/* Row */}
              <Text size="3" color="gray">
                {t("shipping_discount")}
              </Text>
              <Text size="3" color="gray" align="right">
                -
                {priceFormat(totalShippingFee)}
              </Text>

              <div className="col-span-2">
                <Separator size="1" className="my-2" />
              </div>
            </>
          )
        : null}

      {(tableType === "full" || tableType === "tail")
        ? (
            <>
              {/* Row */}
              <div className={`
                flex flex-row items-baseline gap-2
              `}
              >
                <Text size="4" weight="bold">
                  {t("total")}
                </Text>
                <Text size="2" color="gray">
                  {`≈ ${priceFormat(discountedPricePerBox)} / ${humanWeightStr({ grams: boxGrams })}`}
                </Text>
              </div>
              <div className={`
                flex flex-row items-baseline justify-end
                gap-1
              `}
              >
                <Text
                  size="3"
                  color="gray"
                  weight="light"
                  className="line-through"
                >
                  {priceFormat(totalBeforeDiscount)}
                </Text>
                <Text size="4" weight="bold" semanticColor="danger">
                  {priceFormat(total)}
                </Text>
              </div>
            </>
          )
        : null}

    </div>
  );
};

interface SavedBadgeProps {
  data: ServerDataOutput;
  forMobile?: boolean;
}
const SavedBadge = (props: SavedBadgeProps) => {
  const { data, forMobile } = props;

  const {
    totalSave,
    totalSavePercent,
  } = useSubscriptionSummary({ data });

  const t = useTranslations("stf_m_subscription.screen_subscription.summary_section");

  return (
    <div className={cn(
      `
        absolute right-0 top-0 flex -translate-x-2
        bg-danger-2 px-2 py-1 rounded-2 shadow-2
      `,
      forMobile
        ? `-translate-y-8`
        : `
          -translate-y-2
          md:-translate-y-4
        `,
    )}
    >
      <Text
        size="2"
        semanticColor="danger"
        weight="medium"
      >
        {t("you_saved", {
          totalPercent: totalSavePercent,
          totalThb: priceFormat(totalSave),
        })}
      </Text>
    </div>
  );
};

const LoadingSummary = () => {
  // height of filled summary
  return (
    <Skeleton
      className="shadow-2 rounded-2 h-[408px]"
    />
  );
};

interface SummarySectionProps {
  data: ServerDataOutput;
}
export const SummarySection = (props: SummarySectionProps) => {
  const { data } = props;

  return (
    <Suspense fallback={<LoadingSummary />}>
      <div>
        <SavedBadge data={data} />
        <IconSummary data={data} />
        <Separator size="4" className="my-6" />
        <TableSummary data={data} />
      </div>
    </Suspense>
  );
};

export const MobileSummarySection = (props: SummarySectionProps) => {
  const { data } = props;
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="relative">
      <SavedBadge data={data} forMobile />

      <AnimatePresence initial={false} mode="wait">
        <motion.div
          key={expanded ? "expanded" : "collapsed"}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ delay: 0, duration: 0.1, ease: "easeInOut" }}
          className="overflow-hidden"
        >
          {expanded
            ? (
                <div className="mt-6">
                  <IconSummary data={data} />
                  <Separator size="4" className="my-6" />
                  <TableSummary data={data} tableType="head" />
                </div>
              )
            : null}
        </motion.div>
      </AnimatePresence>
      <TableSummary data={data} tableType="tail" />
      <IconButton
        screenReaders="Expand summary detail"
        variant="surface"
        className={`
          absolute left-1/5 top-0 -translate-y-8
          translate-x-1/2 rounded-full
          xxs:right-1/2
        `}
        size="3"
        onClick={() => {
          return setExpanded((prev) => {
            return !prev;
          });
        }}
      >
        {expanded ? <ChevronDownIcon /> : <ChevronUpIcon />}
      </IconButton>
    </div>
  );
};
